import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { resolveProTenantSubdomain } from '@/lib/pro-tenant';
import type { PdfCompanyInfo } from '@erp/pdf';
import type { PublicProPdfTemplate } from '@erp/data-access/interfaces';

interface ProBranding {
  loginLogoUrl: string | null;
  faviconUrl: string | null;
  /** Company identity + raster logo + brand color for generated PDF documents
   *  (devis / commande). Sourced from the tenant + store theme. */
  pdfCompany: PdfCompanyInfo;
  pdfPrimaryColor: string | null;
  /** The tenant's Espace Pro PDF model (Réglages → Modèles PDF). Null when
   *  not customized — PDFs then use the built-in Tunisien design. */
  pdfTemplate: PublicProPdfTemplate | null;
}

const FALLBACK_PDF_COMPANY: PdfCompanyInfo = {
  companyName: 'Espace Pro',
  taxId: null,
  address: null,
  phone: null,
  email: null,
  logoUrl: null,
};

const FALLBACK_BRANDING: ProBranding = {
  loginLogoUrl: '/tunielec-pro-logo.svg',
  faviconUrl: '/tunielec-favicon.svg',
  pdfCompany: FALLBACK_PDF_COMPANY,
  pdfPrimaryColor: null,
  pdfTemplate: null,
};

const ProBrandingContext = createContext<ProBranding>({
  ...FALLBACK_BRANDING,
});

function setFavicon(url: string | null) {
  if (!url || typeof document === 'undefined') return;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

/** Boot outcome. `entitled: false` means the hostname resolved to nothing we
 *  are allowed to serve — either no tenant owns this subdomain, or it does but
 *  never bought Espace Pro. Both cases render the "indisponible" screen instead
 *  of the portal; we don't distinguish them in the UI, so a probe can't use the
 *  message to enumerate which tenants exist. */
type BrandingBoot =
  | { entitled: true; branding: ProBranding }
  | { entitled: false };

export function ProBrandingProvider({ children }: { children: ReactNode }) {
  const dataService = useDataService();
  const tenantSubdomain = resolveProTenantSubdomain();

  const { data, isSuccess, isError } = useQuery({
    queryKey: ['pro-branding', tenantSubdomain],
    // `resolveProTenantSubdomain()` never returns an empty string (it falls
    // back to the default tenant), so there is no "skip the query" case — the
    // old `enabled: tenantSubdomain.length > 0` guard was always true.
    queryFn: async (): Promise<BrandingBoot> => {
      // NOT `getByHostname`: that resolves ANY active tenant, which is how
      // `<anytenant>pro.gesty.tn` used to boot a working portal for a customer
      // who never had the module. `getProPortalTenant` is backed by the
      // `pro_portal_tenant` RPC, which applies the module check server-side.
      const tenant = await dataService.tenants.getProPortalTenant(tenantSubdomain);
      if (!tenant) return { entitled: false };
      // The company identity (name, MF, address, phone, e-mail, RIB, bank,
      // website, fax, tagline, subtitle, socials) comes from the tenant's REAL
      // company_settings — the exact same source the ERP dashboard prints with
      // — so pro devis/commande PDFs carry the full letterhead + legal footer.
      const [theme, identity, pdfTemplate] = await Promise.all([
        dataService.storeTheme.get(tenant.id),
        dataService.tenants.getPublicCompanyIdentity(tenant.id),
        // Defensive: a DB that doesn't have the RPC yet must not block the
        // portal boot — degrade to the built-in Tunisien design.
        dataService.tenants.getPublicProPdfTemplate(tenant.id).catch(() => null),
      ]);
      // Only the LOGO is the pro space's own (the "Logo de connexion" the owner
      // uploads), falling back to the company's PDF logo. pdfmake can't embed
      // SVG, so prefer the raster uploads and never use the bundled SVG here —
      // the PDF design falls back to the company name if none is usable.
      const pdfLogoUrl =
        theme?.proLoginLogoUrl
        ?? theme?.loginLogoUrl
        ?? theme?.logoHorizontalUrl
        ?? theme?.logoSquareUrl
        ?? identity?.pdfLogoUrl
        ?? identity?.logoUrl
        ?? null;
      return {
        entitled: true,
        branding: {
          loginLogoUrl:
            theme?.proLoginLogoUrl
            ?? theme?.loginLogoUrl
            ?? theme?.logoHorizontalUrl
            ?? theme?.logoSquareUrl
            ?? FALLBACK_BRANDING.loginLogoUrl,
          faviconUrl: theme?.proFaviconUrl ?? theme?.faviconUrl ?? FALLBACK_BRANDING.faviconUrl,
          pdfCompany: {
            // No `?? tenant.mf / .phone / .email` here: pro_public_company_identity
            // already coalesces those exact tenant columns server-side, so the old
            // client-side fallbacks were dead weight.
            companyName: identity?.companyName || tenant.name,
            taxId: identity?.taxId ?? null,
            address: identity?.address ?? null,
            phone: identity?.phone ?? null,
            email: identity?.email ?? null,
            fax: identity?.fax ?? null,
            website: identity?.website ?? null,
            rib: identity?.rib ?? null,
            bankName: identity?.bankName ?? null,
            tagline: identity?.tagline ?? null,
            subtitle: identity?.subtitle ?? null,
            socialFacebook: identity?.socialFacebook ?? null,
            socialInstagram: identity?.socialInstagram ?? null,
            socialLinkedin: identity?.socialLinkedin ?? null,
            logoUrl: pdfLogoUrl,
          },
          pdfPrimaryColor: theme?.primaryColor ?? null,
          pdfTemplate,
        },
      };
    },
  });

  const branding = data?.entitled ? data.branding : null;

  useEffect(() => {
    setFavicon(branding?.faviconUrl ?? null);
  }, [branding?.faviconUrl]);

  // A failed boot query used to leave `isSuccess` false forever — an infinite
  // spinner, no message, no way out. It gets its own screen rather than the
  // "indisponible" one: a network blip is not the same as an address we refuse
  // to serve, and telling the user to check the link would be a lie.
  if (isError) return <ProPortalUnavailable reason="error" />;
  if (!isSuccess) return <ProBrandingBoot />;
  if (!data.entitled) return <ProPortalUnavailable reason="unavailable" />;

  return (
    <ProBrandingContext.Provider value={data.branding}>
      {children}
    </ProBrandingContext.Provider>
  );
}

export function useProBranding(): ProBranding {
  return useContext(ProBrandingContext);
}

function ProBrandingBoot() {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: 'var(--pro-bg, #f8fafc)' }}
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-sm space-y-5">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-500" />
        <div className="space-y-3">
          <div className="mx-auto h-3 w-28 animate-pulse rounded-full bg-neutral-200" />
          <div className="h-8 w-full animate-pulse rounded-md bg-neutral-100" />
          <div className="grid grid-cols-3 gap-2">
            <div className="h-16 animate-pulse rounded-md bg-neutral-100" />
            <div className="h-16 animate-pulse rounded-md bg-neutral-100" />
            <div className="h-16 animate-pulse rounded-md bg-neutral-100" />
          </div>
        </div>
        <span className="sr-only">Chargement...</span>
      </div>
    </div>
  );
}

/** Terminal boot screens.
 *
 *  `unavailable` covers all three refusal cases — unknown subdomain, inactive
 *  tenant, tenant without the Espace Pro module — with ONE wording on purpose:
 *  naming the reason would let anyone probe `<x>pro.gesty.tn` to learn which
 *  tenants exist and which of them bought the module.
 *
 *  No i18n here: the portal's translations load below this gate, so these have
 *  to render on their own. FR only, like the rest of the pro portal's shell. */
function ProPortalUnavailable({ reason }: { reason: 'unavailable' | 'error' }) {
  const isError = reason === 'error';
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: 'var(--pro-bg, #f8fafc)' }}
      role="alert"
    >
      <div className="w-full max-w-sm text-center space-y-3">
        <h1 className="text-base font-semibold text-neutral-800">
          {isError ? 'Connexion impossible' : 'Espace Pro indisponible'}
        </h1>
        <p className="text-sm text-neutral-500">
          {isError
            ? 'Nous n’avons pas pu joindre le serveur. Vérifiez votre connexion, puis réessayez.'
            : 'Cette adresse ne correspond à aucun Espace Pro actif. Vérifiez le lien auprès de votre fournisseur.'}
        </p>
        {isError && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-1 rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            Réessayer
          </button>
        )}
      </div>
    </div>
  );
}
